import { useEffect, useRef, useState, useSyncExternalStore } from 'react';

/**
 * «التطبيق المثبَّت» (25 سبتمبر 2026): حين تُفتح المنصة من أيقونة الشاشة الرئيسية لا من
 * المتصفّح، تختفي أدواتُ المتصفّح كلُّها — لا زرّ رجوع ولا إعادة تحميل. فما كان يوفّره
 * المتصفّح يوفّره التطبيق نفسُه: السحبُ للتحديث هنا، والرجوعُ في الترويسة.
 */
const STANDALONE_QUERY = '(display-mode: standalone)';

/** قراءةٌ لحظية خارج React (إشعاراتُ الجوال تسألها في معالج ضغطة) */
export function isStandalone(): boolean { return readStandalone(); }

function readStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  const iosStandalone = (navigator as Navigator & { standalone?: boolean }).standalone === true;
  return iosStandalone || (typeof window.matchMedia === 'function' && window.matchMedia(STANDALONE_QUERY).matches);
}

function subscribeStandalone(cb: () => void): () => void {
  if (typeof window.matchMedia !== 'function') return () => {};
  const mq = window.matchMedia(STANDALONE_QUERY);
  mq.addEventListener('change', cb);
  return () => mq.removeEventListener('change', cb);
}

/** هل تعمل المنصة الآن تطبيقاً مثبّتاً؟ (يتغيّر إن ثُبّتت أثناء الجلسة) */
export function useStandalone(): boolean {
  return useSyncExternalStore(subscribeStandalone, readStandalone, () => false);
}

/**
 * السحبُ للتحديث — في التطبيق المثبَّت وحده: في المتصفّح يملك المتصفّحُ هذه الإيماءة
 * (يعيد تحميل الصفحة كاملةً)، وإيماءتان على الحركة نفسِها تتعاركان.
 *
 * لا يعيد تحميل الصفحة: يستدعي `onRefresh` (إبطالُ الاستعلامات النشطة) فتُجلب البياناتُ
 * من جديد والشاشةُ في مكانها. والمقاومةُ نصفُ المسافة فلا تنفلت الإيماءة، والعتبة 70px.
 */
export function usePullToRefresh(enabled: boolean, onRefresh: () => Promise<unknown>) {
  const [pull, setPull] = useState(0);
  const [busy, setBusy] = useState(false);
  const start = useRef<number | null>(null);
  const distance = useRef(0);
  const refresh = useRef(onRefresh);
  useEffect(() => { refresh.current = onRefresh; }, [onRefresh]);

  useEffect(() => {
    if (!enabled) return;
    const THRESHOLD = 70;
    const onStart = (e: TouchEvent) => {
      // يبدأ من أعلى الصفحة فقط، وبإصبعٍ واحد، وليس داخل نافذةٍ منبثقة أو حقل
      if (window.scrollY > 0 || e.touches.length !== 1) { start.current = null; return; }
      const el = e.target as Element | null;
      // ولا داخل القائمة الجانبية وشريط التبويب ولا والقائمةُ مفتوحة: تمريرُ القائمة لأعلى كان يُقرأ سحباً للتحديث
      if (el?.closest('.modal, input, textarea, select, [data-no-pull], .tabbar') || document.querySelector('.sidebar--open')) { start.current = null; return; }
      start.current = e.touches[0].clientY;
      distance.current = 0;
    };
    const onMove = (e: TouchEvent) => {
      if (start.current === null) return;
      const d = e.touches[0].clientY - start.current;
      if (d <= 0) { distance.current = 0; setPull(0); return; }
      distance.current = Math.min(110, d / 2);
      setPull(distance.current);
    };
    const onEnd = () => {
      if (start.current === null) return;
      start.current = null;
      const go = distance.current >= THRESHOLD;
      distance.current = 0;
      setPull(0);
      if (!go) return;
      setBusy(true);
      void refresh.current().finally(() => setBusy(false));
    };
    window.addEventListener('touchstart', onStart, { passive: true });
    window.addEventListener('touchmove', onMove, { passive: true });
    window.addEventListener('touchend', onEnd, { passive: true });
    window.addEventListener('touchcancel', onEnd, { passive: true });
    return () => {
      window.removeEventListener('touchstart', onStart);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onEnd);
      window.removeEventListener('touchcancel', onEnd);
    };
  }, [enabled]);

  return { pull, busy, ready: pull >= 70 };
}
